/* eslint-disable prettier/prettier */
import React, { useEffect, useRef, useState } from 'react';
import { View, Animated, StyleSheet } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import Header from './header/details';
import EventCharts from './eventsChart';
import Links from './links';
import DetailsComponent from '@components/machines/components/detailsComponents';
import Loading from '@components/loading';

import { height, scale } from '@components/frame';
import {
  isPhone,
  isWeb,
  ALL_MACHINES,
  timePeriod,
  machineDetailsConditions,
  Alert,
  machineStatus,
  ncFileStatuses,
  navigateBack,
  parseRouteParamAsNumber,
} from '../../../helpers';

import useStatusBar from '@components/common/useStatusBar';

import { font } from '@styles/fonts';
import ColorWrapper from '@styles/colors';

import { switchMachine, getMachines } from '@reducers/machines';
import { removeProductionInformation, getNCFileById, getNCFileByName } from '@reducers/manufacture';
import { getPartByID } from '@reducers/parts';
import { ncFilesSearch } from '@reducers/search';

const ScrollWrapper = (props) => {
  return props.scrollDisable ? (
    <View style={[isPhone ? { flex: 1 } : { width: '100%', height: '100%' }]}>{props.children}</View>
  ) : (
    <Animated.ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={[
        { backgroundColor: props.C('Background/Light/Detail'), width: '100%' },
        isWeb && { height: height },
        props.isNow && props.selectedLevel !== 'batch' && { flex: 1 },
      ]}
      scrollEventThrottle={16}
      scrollEnabled={!props.scrollDisable}
      onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: props.fadeAnim } } }], {
        useNativeDriver: false,
      })}
    >
      {props.children}
    </Animated.ScrollView>
  );
};

const Details = (props) => {
  const {
    C,
    detailsWidth,
    selectedTime,
    setSelectedTime,
    route,
    panelViewMode,
    selectedLevel,
    selectedPeriod,
    setSelectedPeriod,
    chartData,
    setChartData,
    lg,
    selectedChartDate,
    setSelectedChartDate,
    onBack,
    machineStatisticsForNCFile,
    isGlobalMachineList,
    getNCFileByName,
    getNCFileById,
    setSelectedLevel,
    selectedNCFileLevel,
    setSelectedNCFileLevel,
    time,
    setModalMissingPIOpen,
    initialLoading,
    clampingTools,
    ncFileId,
    setSelectedNCFile,
    selectedItem,
    isManufacture,
    getPartByID,
    setActionsModalOpen,
    ncFilesSearch,
  } = props;

  const noMachine = true;

  const {
    server: { offset = 0 },
  } = time;

  const [loading, setLoading] = useState(true);
  const [batchText, setBatchText] = useState(null);

  const isNCFile = Boolean(selectedItem?.machineStatus?.ncFile);

  if (isPhone) {
    useStatusBar({
      C,
      background: C('Background/Light/Detail'),
    });
  }

  const initItem = async (id) => {
    if (!id) {
      return;
    }

    const ncFile = await getNCFileById(id);

    if (!ncFile && isManufacture) {
      Alert(i18n.t({ title: 'NC file not found' }));
      navigateBack();
      return;
    }

    if (ncFile) {
      setSelectedNCFile({
        machineStatus: {
          ...ncFile,
          ncFile: ncFile.ncFileName,
          status: machineStatus.NOTPRODUCTIVE,
          productionBatchId: parseRouteParamAsNumber(route.params.productionBatchId),
          partDetails: {
            drawing: ncFile.partDrawingNumber,
            partId: ncFile.partId,
            partName: ncFile.partNumber,
            position: ncFile.partDrawingPosition,
            description: ncFile.partDescription,
            revision: ncFile.partDrawingRevision,
          },
        },
      });
    }
  };

  const initItemByName = async (name) => {
    if (!name) {
      return;
    }

    const ncFile = await getNCFileByName(name);

    if (ncFile) {
      const item = ncFile?.searchResult?.[0];

      if (!item && isManufacture) {
        Alert(i18n.t({ title: 'NC file not found' }));
        navigateBack();
        return;
      }

      setSelectedNCFile({
        machineStatus: {
          ...item,
          ncFile: item.ncFileName,
          status: machineStatus.NOTPRODUCTIVE,
          productionBatchId: parseRouteParamAsNumber(route.params.productionBatchId),
          partDetails: {
            drawing: item.partDrawingNumber,
            partId: item.partId,
            partName: item.partNumber,
            position: item.partDrawingPosition,
            description: item.partDescription,
            revision: item.partDrawingRevision,
          },
        },
      });
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const partIdParam = parseRouteParamAsNumber(route?.params?.partId);
        if (partIdParam && selectedItem?.machineStatus?.partDetails?.partId != partIdParam) {
          const result = await getPartByID(partIdParam);
          const part = result?.searchResult?.[0];
          if (!part) {
            Alert(i18n.t({ title: 'Part not found' }));
            return navigateBack();
          }
          const isOneFile = part.linkedNcFiles?.length === 1;
          let ncFile = {};
          if (isOneFile) {
            const item = await getNCFileById(part.linkedNcFiles?.[0]?.ncFileId);
            if (item) {
              ncFile = item;
            }
          }

          return setSelectedNCFile(
            {
              machineStatus: {
                ...ncFile,
                productionBatchId: parseRouteParamAsNumber(route.params.productionBatchId),

                ncFile: ncFile?.ncFileName,
                status: machineStatus.NOTPRODUCTIVE,
                partDetails: part,
              },
            },
            true
          );
        }

        const itemId = ncFileId || selectedItem?.machineStatus?.ncFileId;
        if (itemId && !selectedItem?.machineStatus?.partDetails?.partId && ncFileId !== selectedItem?.machineStatus?.ncFileId) {
          await initItem(itemId);
          return;
        }

        if (route?.params?.ncFile) {
          await initItemByName(route?.params?.ncFile);
          return;
        }
      } catch (e) {
        console.log(e, 'e');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 100,
      useNativeDriver: false,
    }).start();
  }, [selectedLevel]);

  const allSelected = selectedItem?.machineStatus?.ncFileId === ALL_MACHINES || isGlobalMachineList;
  const isProperties = selectedTime === timePeriod.PROPERTIES;

  const { status, subStatus, ncFileStatus, ncFile } = selectedItem?.machineStatus || {};

  const { machineActive } = machineDetailsConditions({
    status,
    ncFileStatus,
    subStatus,
    ncFileExist: !!ncFile,
  });

  const showMachineInfo = noMachine && (ncFileStatus ? ncFileStatuses.Linked === ncFileStatus : true) ? true : machineActive;

  const headerProps = {
    selectedTime,
    setSelectedTime,
    fadeAnim,
    onBack,
    C,
    allSelected,
    panelViewMode,
    selectedItem,
    noMachine,
    isNCFile,
    clampingTools,
    setSelectedLevel,
    selectedNCFileLevel,
    setSelectedNCFileLevel,
    selectedLevel,
    showMachineInfo,
    isProperties,
    isManufacture,
    setActionsModalOpen,
    batchText,
  };

  const detailsProps = {
    panelViewMode,
    selectedItem,
    C,
    timeOffset: offset,
    selectedLevel,
    setSelectedLevel,
    selectedPeriod,
    setSelectedPeriod,
    showMachineInfo,
    setModalMissingPIOpen,
    noMachine,
    isNCFile,
    lg,
    initialLoading,
    setLocalSelectedNCFile: setSelectedNCFile,
    detailsWidth,
    setBatchText,
    selectedNCFileLevel,
  };

  const renderContent = () => {
    switch (selectedTime) {
      case timePeriod.PROPERTIES:
        return <DetailsComponent {...detailsProps} />;
      case timePeriod.LINKS:
        return <Links {...{ C, ncFilesSearch, ncFileId: selectedItem?.machineStatus?.ncFileId }} />;
      default:
        return (
          <EventCharts
            {...{
              selectedLevel,
              isManufacture,
              selectedTime,
              setSelectedTime,
              selectedChartDate,
              setSelectedChartDate,
              route,
              C,
              id: selectedItem.machineStatus.ncFileId,
              shortName: selectedItem.machineStatus.ncFileName,
              selectedPeriod,
              setSelectedPeriod,
              chartData,
              setChartData,
              machineStatisticsForNCFile,
              allSelected,
              route,
            }}
          />
        );
    }
  };

  if ((!selectedItem?.machineStatus?.ncFileId && !selectedItem?.machineStatus?.partDetails?.partId) || loading) {
    return <Loading background={C('Background/Light/Detail')} />;
  }

  return [
    <View
      key="MachinesDetails"
      style={[
        {
          flex: 1,
          backgroundColor: C('Background/Light/Detail'),
          borderLeftColor: C('Hairline / Light / No trans'),
          borderLeftWidth: isPhone || panelViewMode ? 0 : isWeb ? 1 : StyleSheet.hairlineWidth,
          width: isPhone ? '100%' : detailsWidth - 2,
        },
      ]}
    >
      <View
        style={[
          isPhone
            ? { flex: 1 }
            : {
                width: '100%',
                flex: 1,
              },
        ]}
      >
        <Header {...headerProps} />
        <ScrollWrapper
          {...{
            C,
            scrollDisable:
              (isPhone && ((isProperties && selectedLevel !== 'batch') || (allSelected && selectedPeriod !== 'Paid'))) ||
              (isWeb && height < scale(800)),
            fadeAnim,
            selectedTime,
            selectedLevel,
            font,
            lg,
            selectedChartDate,
            isNow: selectedTime == 'properties',
          }}
        >
          <View
            style={[
              isPhone
                ? { flex: 1 }
                : {
                    width: '100%',
                    // height: height,
                    flex: 1,
                    paddingHorizontal: scale(18),
                  },
              isWeb && { minHeight: scale(800) },
            ]}
          >
            <Header
              {...{
                ...headerProps,
                dummy: true,
              }}
            />
            <View style={[isPhone ? { flex: 1 } : { width: '100%', flex: 1, marginTop: scale(panelViewMode ? 5 : 15) }]}>
              {loading ? <Loading /> : renderContent()}
            </View>
          </View>
        </ScrollWrapper>
      </View>
    </View>,
  ];
};

const mapStateToProps = ({ machines: { machineStatisticsForNCFile }, clampingTools, time, manufacture: { selectedNCFile } }) => ({
  time,
  clampingTools,
  selectedNCFile,
  machineStatisticsForNCFile,
});

export default connect(mapStateToProps, {
  switchMachine,
  removeProductionInformation,
  getMachines,
  getNCFileById,
  getNCFileByName,
  getPartByID,
  ncFilesSearch,
})(ColorWrapper(Details));
